import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import useRequiredFields from "../../../../hooks/useRequiredFields";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import ReceipeDetail from "./ReceipeDetail";

const createFgBatchRow = (overrides = {}) => ({
  id: `${Date.now()}-${Math.random()}`,
  product_id: "",
  batch_size: "",
  receipe_id: "",
  items: [],
  receipeNotFound: "",
  prevBatchSize: 1,
  ...overrides,
});

const mapRecipeItems = (items = [], uidPrefix = "item") =>
  items.map((item, index) => ({
    ...item,
    product_id: item.product_id || item.product?.id || item.id,
    code: item.code || item.product?.code || "",
    name: item.name || item.product?.name || "",
    qty: item.qty ?? item.standard_qty ?? item.unit_qty ?? 1,
    request_qty: item.request_qty ?? item.qty ?? item.standard_qty ?? item.unit_qty ?? 1,
    remarks: item.remarks || "",
    uid:
      item.uid ||
      `${uidPrefix}-${index}-${item.product_id || item.product?.id || item.id}`,
  }));

const buildFgBatchRows = (groups = []) =>
  groups.map((group, index) => ({
    id: group.id || `${Date.now()}-${index}`,
    product_id:
      group.product_group_id ||
      group.product_group?.id ||
      group.product_id ||
      "",
    batch_size: group.batch_size || "",
    receipe_id: group.receipe_id || group.recipe_id || "",
    items: mapRecipeItems(group.items || group.product_group?.group_items || [], `group-${index}`),
    receipeNotFound: "",
    prevBatchSize: parseFloat(group.batch_size) || 1,
  }));

const normalizeRecipeItems = (items = []) =>
  items.map((it) => ({
    ...it,
    request_qty: it.request_qty ?? it.qty ?? 1,
  }));

const scaleRecipeItemsByBatchSize = (items = [], sourceBatchSize = 1, targetBatchSize = 1) => {
  const source = parseFloat(sourceBatchSize) || 1;
  const target = parseFloat(targetBatchSize) || 1;
  const ratio = target / source;

  return items.map((item) => {
    const qty = parseFloat(item.qty ?? item.request_qty ?? 0) || 0;

    return {
      ...item,
      qty: (qty * ratio).toFixed(3),
      request_qty: (qty * ratio).toFixed(3),
    };
  });
};

const Edit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const [product, setProduct] = useState([]);
  const [rmpm, setRmpm] = useState([]);
  const [loading, setLoading] = useState(false);
  const { checkRequiredFields } = useRequiredFields();
  const [fgBatchList, setFgBatchList] = useState([createFgBatchRow()]);
  const [activeRecipeIndex, setActiveRecipeIndex] = useState(0);
  const [combinedItemEdits, setCombinedItemEdits] = useState({});
  const [removedCombinedItems, setRemovedCombinedItems] = useState(new Set());
  const [manualCombinedItems, setManualCombinedItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const getAlreadySelectedItems = () => {
    return fgBatchList
      .map((row) => row.product_id)
      .filter((productId) => productId);
  };

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      try {
        const requisitionRes = await Http.get(`/requisition-production/${id}/edit`);
        const data = requisitionRes.data || {};

        setInputs({
          date: data.date || "",
          time: data.time || "",
          batch_no: data.batch_no || "",
          expected_production_date: data.expected_production_date || data.date || "",
        });

        const groups = Array.isArray(data.requisition_product_groups)
          ? data.requisition_product_groups
          : [];
        const existingItems = Array.isArray(data.items) ? data.items : [];

        if (groups.length > 0) {
          const initialRows = buildFgBatchRows(groups);
          if (existingItems.length > 0) {
            const manualItems = mapRecipeItems(existingItems, "manual-existing");
            setManualCombinedItems(manualItems);
            setCombinedItemEdits(
              Object.fromEntries(
                manualItems.map((item) => [
                  item.uid,
                  {
                    request_qty: item.request_qty,
                    issue_qty: item.issue_qty,
                    qty: item.qty,
                    remarks: item.remarks || "",
                  },
                ])
              )
            );
          } else {
            setManualCombinedItems([]);
            setCombinedItemEdits({});
          }
          setRemovedCombinedItems(new Set());

          const hydratedRows = await Promise.all(
            groups.map(async (group, index) => {
              const productGroupId =
                group.product_group_id || group.product_group?.id || group.product_id || "";

              if (!productGroupId) {
                return initialRows[index];
              }

              try {
                const res = await Http.post("/receipe-load-product-wise", {
                  ...data,
                  product_id: productGroupId,
                  receipe_type: "fifo",
                });

                if (res.data.status == 1) {
                  const recipeItems = normalizeRecipeItems(res.data.info.items || []);
                  const scaledItems = scaleRecipeItemsByBatchSize(
                    recipeItems,
                    res.data.info.batch_size || 1,
                    group.batch_size || res.data.info.batch_size || 1
                  );

                  return {
                    ...initialRows[index],
                    product_id: productGroupId,
                    batch_size: group.batch_size || res.data.info.batch_size || "",
                    receipe_id: res.data.info.id,
                    items: scaledItems,
                    receipeNotFound: "",
                    prevBatchSize:
                      parseFloat(group.batch_size || res.data.info.batch_size) || 1,
                  };
                }

                return {
                  ...initialRows[index],
                  product_id: productGroupId,
                  items: [],
                  receipeNotFound: res.data.msg,
                };
              } catch (err) {
                return {
                  ...initialRows[index],
                  product_id: productGroupId,
                  items: [],
                  receipeNotFound: "Failed to load recipe.",
                };
              }
            })
          );

          setFgBatchList(hydratedRows.length > 0 ? hydratedRows : initialRows);
          setRemovedCombinedItems(new Set());
        } else {
          if (existingItems.length > 0) {
            const manualItems = mapRecipeItems(existingItems, "manual-existing");

            setFgBatchList([
              createFgBatchRow({
                product_id: data.product_id || "",
                batch_size: data.batch_size || "",
                receipe_id: data.receipe_id || "",
                items: [],
                prevBatchSize: parseFloat(data.batch_size) || 1,
              }),
            ]);
            setManualCombinedItems(manualItems);
            setCombinedItemEdits(
              Object.fromEntries(
                manualItems.map((item) => [
                  item.uid,
                  {
                    request_qty: item.request_qty,
                    issue_qty: item.issue_qty,
                    qty: item.qty,
                    remarks: item.remarks || "",
                  },
                ])
              )
            );
          } else {
            setFgBatchList([
              createFgBatchRow({
                product_id: data.product_id || "",
                batch_size: data.batch_size || "",
                receipe_id: data.receipe_id || "",
                items: [],
                prevBatchSize: parseFloat(data.batch_size) || 1,
              }),
            ]);
            setManualCombinedItems([]);
            setCombinedItemEdits({});
          }
        }

        const productRes = await Http.get("/product-group-for-dropdown");
        setProduct(productRes.data);

        const resRmpm = await Http.get(
          "/product-wise-stock?no_stock=1&type=raw_materials-packaging_materials"
        );
        setRmpm(resRmpm.data.item);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    DatatableFunc();
    fetchData();
  }, [id]);

  const addMoreList = (id) => {
    Http.get(`/add-new-rmpm-receipe/${id}?no_stock=1`).then((res) => {
      const targetIndex = activeRecipeIndex;

      setFgBatchList((current) => {
        const next = [...current];
        const targetRow = next[targetIndex];

        if (!targetRow) {
          return current;
        }

        const exists = (targetRow.items || []).some(
          (item) => Number(item.product_id ?? item.id) === Number(id)
        );

        // Allow adding if item was removed from combined view, otherwise show error for true duplicates
        if (exists && !removedCombinedItems.has(Number(id))) {
          Swal.fire({
            title: "Error!",
            text: "This Product already exists in the active recipe.",
            icon: "error",
            confirmButtonText: "Ok",
          });
          return current;
        }

        next[targetIndex] = {
          ...targetRow,
          items: [
            ...(targetRow.items || []),
            {
              ...res.data,
              uid: `manual-${res.data.product_id ?? res.data.id}-${Date.now()}`,
              qty: res.data.qty ?? res.data.standard_qty ?? res.data.unit_qty ?? 1,
              request_qty: 1,
              remarks: "",
            },
          ],
        };

        return next;
      });

      // Remove the item from removedCombinedItems if it's being re-added
      setRemovedCombinedItems((prev) => {
        const next = new Set(prev);
        next.delete(Number(id));
        return next;
      });
    });
  };

  const addFgRow = () => {
    setFgBatchList((prev) => [...prev, createFgBatchRow()]);
  };

  const removeFgRow = (rowIndex) => {
    if (fgBatchList.length === 1) {
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setFgBatchList((prev) => {
          const next = prev.filter((_, idx) => idx !== rowIndex);
          setActiveRecipeIndex((current) =>
            Math.min(current, Math.max(next.length - 1, 0))
          );
          return next.length > 0 ? next : [createFgBatchRow()];
        });
      }
    });
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleProductChange = (selectedOption, rowIndex) => {
    const value = selectedOption?.value;
    setActiveRecipeIndex(rowIndex);
    setError({});
    setFgBatchList((prev) =>
      prev.map((row, idx) =>
        idx === rowIndex
          ? {
            ...row,
            product_id: value,
            receipeNotFound: "",
          }
          : row
      )
    );

    if (!value) {
      return;
    }

    const payload = {
      ...inputs,
      product_id: value,
      receipe_type: "fifo",
    };

    Http.post("/receipe-load-product-wise", payload).then((res) => {
      if (res.data.status == 1) {
        const selectedBatchSize =
          parseFloat(selectedOption?.batch_size) ||
          parseFloat(fgBatchList[rowIndex]?.batch_size) ||
          parseFloat(res.data.info.batch_size) ||
          1;
        const itemsWithRequest = scaleRecipeItemsByBatchSize(
          normalizeRecipeItems(res.data.info.items || []),
          res.data.info.batch_size || 1,
          selectedBatchSize
        );
        const newProductIds = new Set(
          (res.data.info.items || []).map(
            (item) => Number(item.product_id || item.product?.id || item.id)
          )
        );
        setRemovedCombinedItems((prev) => {
          const next = new Set(prev);
          newProductIds.forEach((id) => next.delete(id));
          return next;
        });

        setFgBatchList((prev) =>
          prev.map((row, idx) => {
            if (idx !== rowIndex) {
              return row;
            }

            return {
              ...row,
              receipe_id: res.data.info.id,
              batch_size: row.batch_size || selectedBatchSize,
              items: itemsWithRequest,
              receipeNotFound: "",
              prevBatchSize: selectedBatchSize,
            };
          })
        );
      } else {
        setFgBatchList((prev) =>
          prev.map((row, idx) =>
            idx === rowIndex
              ? {
                ...row,
                items: [],
                receipeNotFound: res.data.msg,
              }
              : row
          )
        );
      }
    });
  };
  const handleBatchSizeChange = (e, rowIndex) => {
    const { value } = e.target;
    setActiveRecipeIndex(rowIndex);

    setFgBatchList((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIndex) {
          return row;
        }

        const newBatchSize = parseFloat(value) || 1;
        const oldBatchSize = parseFloat(row.prevBatchSize) || 1;

        const scaledItems = (row.items || []).map((item) => {
          const baseQty =
            parseFloat(item.base_qty ?? item.qty ?? item.request_qty) || 0;

          const newQty = (baseQty / oldBatchSize) * newBatchSize;

          return {
            ...item,
            base_qty: baseQty,
            qty: newQty.toFixed(3),
            request_qty: newQty.toFixed(3),
          };
        });

        return {
          ...row,
          batch_size: value,
          prevBatchSize: newBatchSize,
          items: scaledItems,
        };
      })
    );
  };

  const handleRecipeItemChange = (rowIndex, itemIndex, e) => {
    const { name, value } = e.target;

    setFgBatchList((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIndex) {
          return row;
        }

        const nextItems = [...(row.items || [])];
        nextItems[itemIndex] = {
          ...nextItems[itemIndex],
          [name]: value,
        };

        return {
          ...row,
          items: nextItems,
        };
      })
    );
  };
  console.log("fgBatchList", fgBatchList);
  console.log("manualCombinedItems", manualCombinedItems);
  const getCombinedRecipeItems = () => {
    const combinedMap = new Map();
    console.log(fgBatchList);

    fgBatchList.forEach((row) => {
      (row.items || []).forEach((item) => {
        const productId = Number(item.product_id ?? item.product?.id ?? item.id);

        if (!productId) {
          return;
        }

        const qty = Number(item.request_qty ?? item.qty ?? 0) || 0;
        const existing = combinedMap.get(productId);

        if (existing) {
          existing.qty = Number(existing.qty) + qty;
          existing.request_qty = Number(existing.request_qty) + qty;
        } else {
          combinedMap.set(productId, {
            ...item,
            uid: `recipe-${productId}`,
            qty,
            request_qty: qty,
          });
        }
      });
    });


    // manualCombinedItems.forEach((item) => {
    //   const productId = Number(item.product_id);

    //   if (combinedMap.has(productId)) {
    //     combinedMap.set(productId, {
    //       ...combinedMap.get(productId),

    //       qty: Number(item.qty || 0),
    //       request_qty: Number(item.qty || 0),
    //       stndrd_qty: Number(item.stndrd_qty || 0),

    //       uid: item.uid,
    //     });

    //     return;
    //   }

    //   combinedMap.set(productId, {
    //     ...item,
    //     uid: item.uid || `manual-${productId}`,
    //   });
    // });
    manualCombinedItems.forEach((item) => {
      const productId = Number(item.product_id);

      if (combinedMap.has(productId)) {
        const recipeItem = combinedMap.get(productId);

        combinedMap.set(productId, {
          ...recipeItem,
          qty: Number(recipeItem.qty || 0),
          request_qty: Number(recipeItem.request_qty || 0),
          remarks: recipeItem.remarks || item.remarks || "",
          uid: recipeItem.uid,
        });

        return;
      }
      combinedMap.set(productId, {
        ...item,
        qty: Number(item.qty || 0),
        request_qty: Number(item.request_qty || item.qty || 0),
        uid: item.uid || `manual-${productId}`,
      });
    });
    return Array.from(combinedMap.values()).map((item) => ({
      ...item,
      qty: (Number(item.qty) || 0).toFixed(3),
      request_qty: (Number(item.request_qty) || 0).toFixed(3),
    }));
  };

  const displayCombinedRecipeItems = getCombinedRecipeItems();

  const finalCombinedRecipeItems = displayCombinedRecipeItems
    .filter((item) => !removedCombinedItems.has(Number(item.product_id ?? item.id)))
    .map((item) => {
      const edit = combinedItemEdits[item.uid];

      return {
        ...item,
        request_qty: edit?.request_qty ?? item.request_qty,
        issue_qty: edit?.issue_qty ?? (item.issue_qty || 0),
        stndrd_qty: edit?.qty ?? item.stndrd_qty ?? item.qty ?? 0,
        remarks: edit?.remarks ?? item.remarks ?? "",
      };
    });

  const handleCombinedItemChange = (e, productId) => {
    const { name, value } = e.target;

    setCombinedItemEdits((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [name]: value,
      },
    }));
  };

  const handleCombinedItemRemove = (item) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setRemovedCombinedItems((prev) => {
          const next = new Set(prev);
          next.add(Number(item.product_id ?? item.id));
          return next;
        });
      }
    });
  };

  const formSubmit = () => {
    checkRequiredFields();

    const fgBatchItems = fgBatchList.map((row) => ({
      product_id: row.product_id || "",
      batch_size: row.batch_size || "",
      receipe_id: row.receipe_id || "",
      stndrd_qty: row.qty || "",
      items: row.items || [],
    }));

    const submitData = {
      ...inputs,
      product_id: fgBatchList[0]?.product_id || inputs.product_id || "",
      batch_size: fgBatchList[0]?.batch_size || inputs.batch_size || "",
      receipe_id: fgBatchList[0]?.receipe_id || inputs.receipe_id || "",
      items: finalCombinedRecipeItems,
      fg_batch_items: fgBatchItems,
    };

    setSubmitting(true);
    Http.put("/requisition-production/" + id, submitData)
      .then((res) => {
        setSubmitting(false);
        navigate("/requisition-production-with-batch", {
          state: { message: res.data.message, type: "success" },
        });
      })
      .catch(function (e) {
        setSubmitting(false);
        setError(e.response.data.errors);
      });
  };

  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small">{error[fieldName]}</div>;
    }
    return null;
  };

  const cols = [
    { field: (item) => item.code + "-" + item.name, header: "Item" },
    { field: (item) => item.unit_qty + " " + item.unit_name, header: "Unit" },
    { field: "stockQty", header: "Stock" },
  ];

  const actionTemplate = (item) => {
    return (
      <td className="text-right">
        <button
          className="btn btn-success btn-sm"
          onClick={() => addMoreList(item.id)}
        >
          <i className="fa fa-plus"></i>
        </button>
      </td>
    );
  };
  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Edit Requisition Production With Batch Size</h5>
        </div>
        <div className="am-pagebody row d-flex justify-content-evenly">
          <div className="card pd-20 pd-sm-40 col-7">
            <h6 className="card-body-title mg-sm-b-30 text-center">
              Edit Requisition Production Form
            </h6>
            <hr />
            {loading ? (
              <ProgressSpinner />
            ) : (
              <>
                <div className="row">
                  <div className="col-lg">
                    <label>
                      Date <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={inputs.date || ""}
                      onChange={handleChange}
                      placeholder="Input Date"
                      data-required
                    />
                    {displayError("date")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Time <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      name="time"
                      value={inputs.time || ""}
                      onChange={handleChange}
                      placeholder="Input Time"
                      data-required
                    />
                    {displayError("time")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Req. No. <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="batch_no"
                      className="form-control"
                      name="batch_no"
                      value={inputs.batch_no || ""}
                      onChange={handleChange}
                      placeholder="Input Batch No"
                      data-required
                    />
                    {displayError("batch_no")}
                  </div>
                </div>

                {fgBatchList.map((item, idx) => (
                  <div className="p-1" key={item.id}>
                    <div className="d-flex align-items-start">
                      <div className="col-lg">
                        <label htmlFor={`product_id_${idx}`}>
                          Finished Good Group<i className="fa fa-star required"></i>
                        </label>
                        <Select
                          className="basic-single"
                          classNamePrefix="select"
                          name="product_id"
                          options={product.filter(
                            (opt) => !getAlreadySelectedItems().includes(Number(opt.value))
                          )}
                          value={
                            product.find((opt) => Number(opt.value) === Number(item.product_id)) || null
                          }
                          onChange={(selected) => handleProductChange(selected, idx)}
                          data-required
                        />
                        {idx === 0 && displayError("product_id")}
                      </div>
                      <div className="col-lg ms-3">
                        <label htmlFor={`batch_size_${idx}`}>
                          Batch Size <i className="fa fa-star required"></i>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id={`batch_size_${idx}`}
                          name="batch_size"
                          value={item.batch_size}
                          onChange={(e) => handleBatchSizeChange(e, idx)}
                          placeholder="Input Batch Size"
                          data-required
                          readOnly
                        />
                        {idx === 0 && displayError("batch_size")}
                      </div>
                      <div className="ms-3 d-flex align-items-center" style={{ minHeight: 74 }}>
                        {fgBatchList.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm ms-2"
                            onClick={() => removeFgRow(idx)}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm mb-2"
                  onClick={addFgRow}
                >
                  <i className="fa fa-plus"></i> Add More
                </button>

                <div className="row mt-3">
                  <div className="card-solid">
                    <div className="card-header with-border text-center">
                      <h5 className="card-title mb-0">Combined Recipe Items</h5>
                    </div>
                    <div className="card-body mt-3 p-0 pt-3">
                      <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0">
                        <thead>
                          <tr>
                            <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">Code</th>
                            <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">Product</th>
                            <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">Qty</th>
                            <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">Request Qty</th>
                            <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">Remarks</th>
                            <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {finalCombinedRecipeItems.length > 0 ? (
                            finalCombinedRecipeItems.map((item, index) => (
                              <ReceipeDetail
                                key={`${item.product_id ?? item.id}-${index}`}
                                listInfo={item}
                                listSl={index}
                                error={error}
                                rmpm={rmpm}
                                noStockCheck={1}
                                remove={() => handleCombinedItemRemove(item)}
                                listInputChange={(e) => handleCombinedItemChange(e, item.uid)}
                              />
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6}>
                                <p className="text-center mt-3 mb-3 text-danger">
                                  No recipe items found yet.
                                </p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-3">
                    <button
                      className="btn btn-primary btn-block mg-b-10"
                      onClick={formSubmit}
                      disabled={submitting}
                    >
                      <i className="fa fa-send mg-r-10"></i> Update
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <div
            className="card pd-20 pd-sm-40 col-4"
            style={{ backgroundColor: "#ffffffc7" }}
          >
            <h6 className="card-body-title mg-sm-b-30 text-center">
              Raw &amp; Packaging Materials
            </h6>
            <hr />
            <ReactDataTable
              cols={cols}
              receiveData={rmpm}
              isLoading={loading}
              actionTemplate={actionTemplate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Edit;
